import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import { Style } from '@react-pdf/types';
import { pdfSharedStyles } from '../../pdfSharedStyles';

// Treats "-", "", null, undefined as "unit is absent" for PDF report tables.
// Different producers use different conventions (Airport: '', others: '-'), so
// this predicate unifies them. A React element passed as `unit` is considered
// present (never empty) — the predicate only recognises the 4 primitive sentinels.
// PdfRow callers apply a stricter `typeof unit === 'string'` guard before calling
// this, to keep the auto-promotion logic conservative in legacy codepaths.
export const isEmptyUnit = (unit: unknown): boolean => unit === undefined || unit === null || unit === '' || unit === '-';

interface ValueUnitCellsProps {
    value: React.ReactNode;
    unit: React.ReactNode;
    /** Merged cell width when unit is empty. Default '40%' (standard 35/25/20/20 tables). Use '50%' for 30/20/30/20 tables. */
    mergedWidth?: string;
    /** Split-mode value cell style override (e.g. `{ width: '30%' }` for custom tables). */
    valueStyle?: Style;
    /** Split-mode unit cell style override. */
    unitStyle?: Style;
}

/**
 * Renders either 2 cells (Value + Unit) or 1 merged cell (Value only, widened)
 * based on whether `unit` is empty/dash/undefined. Text is centered via `pdfSharedStyles.tableCell`.
 *
 * Accepts React.ReactNode for both value and unit (supports subscript symbols etc.).
 * Merge is triggered only when `unit` is a string matching empty-unit semantics
 * (via `isEmptyUnit`). A React element unit is always treated as present.
 */
export const ValueUnitCells: React.FC<ValueUnitCellsProps> = ({ value, unit, mergedWidth = '40%', valueStyle, unitStyle }: ValueUnitCellsProps) => {
    const shouldMerge = isEmptyUnit(unit);
    if (shouldMerge) {
        return (
            <View style={[pdfSharedStyles.tableColValue, { width: mergedWidth }]}>
                <Text style={pdfSharedStyles.tableCell}>{value}</Text>
            </View>
        );
    }
    const valueStyles: Style[] = valueStyle ? [pdfSharedStyles.tableColValue, valueStyle] : [pdfSharedStyles.tableColValue];
    const unitStyles: Style[] = unitStyle ? [pdfSharedStyles.tableColUnit, unitStyle] : [pdfSharedStyles.tableColUnit];
    return (
        <>
            <View style={valueStyles}>
                <Text style={pdfSharedStyles.tableCell}>{value}</Text>
            </View>
            <View style={unitStyles}>
                <Text style={pdfSharedStyles.tableCell}>{unit}</Text>
            </View>
        </>
    );
};
